import {
  CUSTOMER_GROUP_EXPORT_FORMATS,
  CustomerGroupExportFormat,
  CustomerGroupExportSettings,
  CustomerGroupWithExportSettings,
} from "../../../types/customer-group-export.type";
import { PurchaseOrderChain } from "../../../types/purchase-order.type";
import { PURCHASE_ORDER_CHAIN_ORDER } from "../../PurchaseOrder/data";

/** "" is the select's "none" option: no export layout / no PO chain. */
export interface MtExportDraft {
  exportFormat: CustomerGroupExportFormat | "";
  filePrefix: string;
  siteLength: string;
  docType: string;
  vatBranchNo: string;
  poChain: PurchaseOrderChain | "";
  poPrefixes: string[];
}

export type MtExportDraftField = Exclude<keyof MtExportDraft, "exportFormat">;

export type MtExportDraftErrors = Partial<Record<MtExportDraftField | "vendor", string>>;

/** Tesco and 7-Eleven share the line-item layout: vendor, doc type, VAT branch and store site. */
const LINE_ITEM_FORMATS: CustomerGroupExportFormat[] = ["tesco", "seven_eleven"];

/** Letters, digits, "_" and "-": the value lands in a download filename (mirrors the API). */
const EXPORT_CODE_PATTERN = /^[A-Za-z0-9_-]+$/;
/** Revenue Department branch number: 5 digits, 00000 = head office. */
const VAT_BRANCH_NO_PATTERN = /^\d{5}$/;
/** A store site is the last N characters of a customer code, which is 10 characters long. */
const MIN_SITE_LENGTH = 1;
const MAX_SITE_LENGTH = 10;

const REQUIRED_MESSAGE = "กรุณาระบุ";

export const usesLineItemLayout = (format: MtExportDraft["exportFormat"]): boolean =>
  format !== "" && LINE_ITEM_FORMATS.includes(format);

/** A select's value as a layout; anything that is not one reads as "no export". */
export const parseExportFormat = (value: string): MtExportDraft["exportFormat"] =>
  CUSTOMER_GROUP_EXPORT_FORMATS.find((format) => format === value) ?? "";

/** A select's value as a PO chain; anything that is not one reads as "no chain". */
export const parsePoChain = (value: string): MtExportDraft["poChain"] =>
  PURCHASE_ORDER_CHAIN_ORDER.find((chain) => chain === value) ?? "";

/** A multi-select's value as a sorted prefix list. */
export const parsePrefixes = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string").sort()
    : [];

export const toMtExportDraft = (group: CustomerGroupWithExportSettings): MtExportDraft => ({
  exportFormat: group.export_format ?? "",
  filePrefix: group.export_file_prefix ?? "",
  siteLength: group.export_site_length === null ? "" : String(group.export_site_length),
  docType: group.export_doc_type ?? "",
  vatBranchNo: group.export_vat_branch_no ?? "",
  poChain: group.po_chain ?? "",
  poPrefixes: group.po_customer_code_prefixes ?? [],
});

const exportLayoutErrors = (draft: MtExportDraft, vendorCode: string | null): MtExportDraftErrors => {
  if (draft.exportFormat === "") return {};

  const errors: MtExportDraftErrors = {};
  if (vendorCode === null) errors.vendor = "กลุ่มนี้ยังไม่มีรหัส vendor จึงออกไฟล์ export ไม่ได้";
  if (!EXPORT_CODE_PATTERN.test(draft.filePrefix)) {
    errors.filePrefix = draft.filePrefix ? "ใช้ได้เฉพาะ A-Z, 0-9, _ และ -" : REQUIRED_MESSAGE;
  }
  if (!usesLineItemLayout(draft.exportFormat)) return errors;

  const siteLength = Number(draft.siteLength);
  const isSiteLengthValid =
    draft.siteLength !== "" &&
    Number.isInteger(siteLength) &&
    siteLength >= MIN_SITE_LENGTH &&
    siteLength <= MAX_SITE_LENGTH;
  if (!isSiteLengthValid) errors.siteLength = `จำนวนเต็ม ${MIN_SITE_LENGTH}–${MAX_SITE_LENGTH}`;
  if (!EXPORT_CODE_PATTERN.test(draft.docType)) {
    errors.docType = draft.docType ? "ใช้ได้เฉพาะ A-Z, 0-9, _ และ -" : REQUIRED_MESSAGE;
  }
  if (!VAT_BRANCH_NO_PATTERN.test(draft.vatBranchNo)) errors.vatBranchNo = "ตัวเลข 5 หลัก";
  return errors;
};

const poErrors = (draft: MtExportDraft): MtExportDraftErrors => {
  const hasChain = draft.poChain !== "";
  const hasPrefixes = draft.poPrefixes.length > 0;
  if (hasChain && !hasPrefixes) return { poPrefixes: "เลือก prefix อย่างน้อย 1 ตัว" };
  if (!hasChain && hasPrefixes) return { poChain: REQUIRED_MESSAGE };
  return {};
};

/**
 * What stops the settings from being saved, per field, in the same terms the API refuses
 * them: an export layout needs the group's vendor code and every column that layout renders,
 * and a PO chain only means something together with its customer-code prefixes.
 */
export const validateMtExportDraft = (
  draft: MtExportDraft,
  vendorCode: string | null,
): MtExportDraftErrors => ({
  ...exportLayoutErrors(draft, vendorCode),
  ...poErrors(draft),
});

/** The PUT body: settings the chosen layout does not render are cleared, never kept stale. */
export const toExportSettingsRequest = (draft: MtExportDraft): CustomerGroupExportSettings => {
  const hasLayout = draft.exportFormat !== "";
  const isLineItem = usesLineItemLayout(draft.exportFormat);

  return {
    export_format: draft.exportFormat || null,
    export_file_prefix: hasLayout ? draft.filePrefix : null,
    export_site_length: isLineItem ? Number(draft.siteLength) : null,
    export_doc_type: isLineItem ? draft.docType : null,
    export_vat_branch_no: isLineItem ? draft.vatBranchNo : null,
    po_chain: draft.poChain || null,
    po_customer_code_prefixes: draft.poPrefixes.length > 0 ? draft.poPrefixes : null,
  };
};
